import { useEffect, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { wipePersonalizationApi } from "@/api/home-context"
import { getPrismWipeStatusApi, PrismMemoryError } from "@/api/memory"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

export function usePersonalizationWipe(getToken: () => string | undefined, onWiped: () => void) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const prismMemory = usePrismMemory()
  const [removalPending, setRemovalPending] = useState(false)
  const [removalAwaitingPrism, setRemovalAwaitingPrism] = useState(false)
  const [removalStatusError, setRemovalStatusError] = useState(false)

  useEffect(() => {
    if (!removalPending) return
    const controller = new AbortController()
    const isCancelled = () => controller.signal.aborted
    let delay = 1000
    let reportedError = false
    let timer: ReturnType<typeof setTimeout>

    async function pollWipeStatus() {
      try {
        const result = await getPrismWipeStatusApi(getToken())
        if (isCancelled()) return
        if (result.complete) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["home-context"] }),
            queryClient.invalidateQueries({ queryKey: ["memories"] }),
            queryClient.invalidateQueries({ queryKey: ["prism-memory"] }),
            queryClient.invalidateQueries({ queryKey: queryKeys.config }),
          ])
          if (!isCancelled()) {
            setRemovalPending(false)
            toast.success(t("personalization.whatWeKnow.danger.wipeSuccess"))
          }
          return
        }
      } catch (error) {
        if (isCancelled()) return
        if (
          error instanceof PrismMemoryError &&
          ((error.status === 404 && error.code === "disabled") ||
            (error.status === 403 && error.code === "blocked_authority") ||
            (error.status === 401 && error.code === "pending_auth"))
        ) {
          setRemovalPending(false)
          setRemovalStatusError(true)
          logger.error("wipe status failed", error)
          toast.error(t("personalization.whatWeKnow.danger.wipeStatusStopped"))
          return
        }
        if (!reportedError) {
          logger.error("wipe status failed", error as Error)
          toast.error(t("personalization.whatWeKnow.danger.wipeStatusFailed"))
          reportedError = true
        }
      }
      if (!isCancelled()) {
        delay = Math.min(delay * 2, 30000)
        timer = setTimeout(pollWipeStatus, delay)
      }
    }

    timer = setTimeout(pollWipeStatus, delay)
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [removalPending, getToken, queryClient, t])

  const wipeMutation = useMutation({
    mutationFn: async () => {
      const result = await wipePersonalizationApi(getToken())
      if (result.ok === false) throw new Error("Wipe request failed")
      return result
    },
    onSuccess: (result) => {
      setRemovalPending(false)
      setRemovalAwaitingPrism(false)
      setRemovalStatusError(false)
      void queryClient.invalidateQueries({ queryKey: ["home-context"] })
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      void queryClient.invalidateQueries({ queryKey: ["prism-memory"] })
      if (result.complete === false && prismMemory && result.prism) {
        setRemovalPending(true)
      } else if (result.complete === false && !prismMemory && result.prism) {
        setRemovalAwaitingPrism(true)
      } else if (result.complete === false || result.failed_count > 0) {
        toast.error(t("personalization.whatWeKnow.danger.wipePartialSuccess"))
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.config })
        toast.success(t("personalization.whatWeKnow.danger.wipeSuccess"))
      }
      onWiped()
    },
    onError: (error) => {
      logger.error("wipe failed", error as Error)
      toast.error(t("personalization.whatWeKnow.danger.wipeFailed"))
    },
  })

  return { wipeMutation, removalPending, removalAwaitingPrism, removalStatusError }
}
